import { Suspense, useEffect } from "react";
import { lazyWithRetry } from "@/lib/lazy-with-retry";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { CompareProvider } from "@/contexts/CompareContext";
import { CompareBar } from "@/components/cars/CompareBar";
import { SentryErrorBoundary } from "@/components/ErrorBoundary";
import { VersionChecker } from "@/components/VersionChecker";
import ScrollToTop from "@/components/ScrollToTop";
import { checkAndUpdateVersion, APP_VERSION } from "@/lib/version";
import { CookieBanner } from "@/components/consent/CookieBanner";
import { usePageViewTracker } from "@/hooks/usePageViewTracker";
import { useGaPageViews } from "@/hooks/useGaPageViews";

// Eagerly loaded public pages
import Index from "./pages/Index";
import { LaunchHome } from "./components/launch/LaunchHome";
const ComingSoon = lazyWithRetry(() => import('./pages/ComingSoon'));
const LaunchConfirm = lazyWithRetry(() => import('./pages/LaunchConfirm'));
import Cars from "./pages/Cars";
const MyCars = lazyWithRetry(() => import("./pages/MyCars"));
const LandingPage = lazyWithRetry(() => import("./pages/LandingPage"));
const Guides = lazyWithRetry(() => import("./pages/Guides"));
const ModelDetail = lazyWithRetry(() => import("./pages/ModelDetail"));
const AdminTasks = lazyWithRetry(() => import("./pages/admin/AdminTasks"));
const AdminLandingPages = lazyWithRetry(() => import("./pages/admin/AdminLandingPages"));
import CarDetail from "./pages/CarDetail";

// Lazy loaded public pages
const Login = lazyWithRetry(() => import("./pages/Login"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const Compare = lazyWithRetry(() => import("./pages/Compare"));
const Brands = lazyWithRetry(() => import("./pages/Brands"));
const BrandDetail = lazyWithRetry(() => import("./pages/BrandDetail"));
const News = lazyWithRetry(() => import("./pages/News"));
const NewsDetail = lazyWithRetry(() => import("./pages/NewsDetail"));
const Campaigns = lazyWithRetry(() => import("./pages/Campaigns"));
const Reviews = lazyWithRetry(() => import("./pages/Reviews"));
const PublisherReviews = lazyWithRetry(() => import("./pages/PublisherReviews"));
const Okonomi = lazyWithRetry(() => import("./pages/Okonomi"));
const Contribute = lazyWithRetry(() => import("./pages/Contribute"));
const Newsletter = lazyWithRetry(() => import("./pages/Newsletter"));
const NewsletterConfirm = lazyWithRetry(() => import("./pages/NewsletterConfirm"));
const NewsletterUnsubscribe = lazyWithRetry(() => import("./pages/NewsletterUnsubscribe"));
const Unsubscribe = lazyWithRetry(() => import("./pages/Unsubscribe"));
const FindCar = lazyWithRetry(() => import("./pages/FindCar"));
const DealerDashboard = lazyWithRetry(() => import("./pages/DealerDashboard"));
const DealerLanding = lazyWithRetry(() => import("./pages/dealer/DealerLanding"));
const DealerActivate = lazyWithRetry(() => import("./pages/dealer/DealerActivate"));
const DealerOnboarding = lazyWithRetry(() => import("./pages/dealer/DealerOnboarding"));
const DealerPortal = lazyWithRetry(() => import("./pages/dealer/DealerPortal"));
const DealerEditVariant = lazyWithRetry(() => import("./pages/dealer/DealerEditVariant"));
const AdminMediaMonitor = lazyWithRetry(() => import("./pages/admin/AdminMediaMonitor"));
const AdminDealerContributions = lazyWithRetry(() => import("./pages/admin/AdminDealerContributions"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const Persondatapolitik = lazyWithRetry(() => import("./pages/Persondatapolitik"));
const Vilkar = lazyWithRetry(() => import("./pages/Vilkar"));
const Cookies = lazyWithRetry(() => import("./pages/Cookies"));
const About = lazyWithRetry(() => import("./pages/About"));
const Faq = lazyWithRetry(() => import("./pages/Faq"));
const Vaerktoejer = lazyWithRetry(() => import("./pages/Vaerktoejer"));
const Magazine = lazyWithRetry(() => import("./pages/Magazine"));
const MagazineIssue = lazyWithRetry(() => import("./pages/MagazineIssue"));


// Lazy loaded admin pages
const AdminDashboard = lazyWithRetry(() => import("./pages/admin/Dashboard"));
const AdminBrands = lazyWithRetry(() => import("./pages/admin/AdminBrands"));
const AdminModels = lazyWithRetry(() => import("./pages/admin/AdminModels"));
const AdminVariants = lazyWithRetry(() => import("./pages/admin/AdminVariants"));
const AdminPrices = lazyWithRetry(() => import("./pages/admin/AdminPrices"));
const AdminReviews = lazyWithRetry(() => import("./pages/admin/AdminReviews"));
const AdminReviewSubmissions = lazyWithRetry(() => import("./pages/admin/AdminReviewSubmissions"));
const AdminNews = lazyWithRetry(() => import("./pages/admin/AdminNews"));
const AdminImport = lazyWithRetry(() => import("./pages/admin/AdminImport"));
const AdminInspirationFilters = lazyWithRetry(() => import("./pages/admin/AdminInspirationFilters"));
const AdminCampaigns = lazyWithRetry(() => import("./pages/admin/AdminCampaigns"));
const AdminDealerCampaigns = lazyWithRetry(() => import("./pages/admin/AdminDealerCampaigns"));
const AdminAnalytics = lazyWithRetry(() => import("./pages/admin/AdminAnalytics"));
const AdminFeedback = lazyWithRetry(() => import("./pages/admin/AdminFeedback"));
const AdminSubmissionErrors = lazyWithRetry(() => import("./pages/admin/AdminSubmissionErrors"));
const AdminDisplaySettings = lazyWithRetry(() => import("./pages/admin/AdminDisplaySettings"));
const AdminSiteSettings = lazyWithRetry(() => import("./pages/admin/AdminSiteSettings"));
const AdminChangeRequests = lazyWithRetry(() => import("./pages/admin/AdminChangeRequests"));
const AdminIdeas = lazyWithRetry(() => import("./pages/admin/AdminIdeas"));
const AdminDealerLinks = lazyWithRetry(() => import("./pages/admin/AdminDealerLinks"));
const AdminCommunity = lazyWithRetry(() => import("./pages/admin/AdminCommunity"));
const AdminExpertApplications = lazyWithRetry(() => import("./pages/admin/AdminExpertApplications"));
const AdminNewsletter = lazyWithRetry(() => import("./pages/admin/AdminNewsletter"));
const AdminCoverage = lazyWithRetry(() => import("./pages/admin/AdminCoverage"));
const AdminDataQuality = lazyWithRetry(() => import("./pages/admin/AdminDataQuality"));
const AdminCsvImport = lazyWithRetry(() => import("./pages/admin/AdminCsvImport"));
const AdminImportReview = lazyWithRetry(() => import("./pages/admin/AdminImportReview"));
const AdminEtl = lazyWithRetry(() => import("./pages/admin/AdminEtl"));

const AdminOverview = lazyWithRetry(() => import("./pages/admin/AdminOverview"));
const AdminCrawl = lazyWithRetry(() => import("./pages/admin/AdminCrawl"));
const AdminUsers = lazyWithRetry(() => import("./pages/admin/AdminUsers"));
const AdminMagazine = lazyWithRetry(() => import("./pages/admin/AdminMagazine"));
const AdminAffiliates = lazyWithRetry(() => import("./pages/admin/AdminAffiliates"));


// Community pages
const CommunityHome = lazyWithRetry(() => import("./pages/community/CommunityHome"));
const AskQuestion = lazyWithRetry(() => import("./pages/community/AskQuestion"));
const PostDetail = lazyWithRetry(() => import("./pages/community/PostDetail"));
const Experiences = lazyWithRetry(() => import("./pages/community/Experiences"));
const ShareExperience = lazyWithRetry(() => import("./pages/community/ShareExperience"));
const Experts = lazyWithRetry(() => import("./pages/community/Experts"));
const HowToBecomeExpert = lazyWithRetry(() => import("./pages/community/HowToBecomeExpert"));
const ModelCommunity = lazyWithRetry(() => import("./pages/community/ModelCommunity"));
const UserProfile = lazyWithRetry(() => import("./pages/community/UserProfile"));
const MyProfile = lazyWithRetry(() => import("./pages/community/MyProfile"));

// Check for version update on app load and clear caches if needed
const versionUpdated = checkAndUpdateVersion();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

if (versionUpdated) {
  queryClient.clear();
  console.log(`[Version] Query cache cleared for version ${APP_VERSION}`);
}

function PageFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-spin w-8 h-8 border-4 border-accent border-t-transparent rounded-full" />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isAdmin, isLoading } = useAuth();
  
  if (isLoading) return <PageFallback />;
  if (!user || !isAdmin) return <Navigate to="/login" replace />;
  
  return <>{children}</>;
}

function AppRoutes() {
  usePageViewTracker();
  useGaPageViews();
  useEffect(() => {
    import("@/lib/affiliate-registry").then((m) => m.hydrateAffiliateRegistry());
  }, []);
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LaunchHome />} />
        <Route path="/kommer-snart" element={<ComingSoon />} />
        <Route path="/lanceringsnyt/bekraeft" element={<LaunchConfirm />} />
        <Route path="/lanceringsnyt/afmeld" element={<LaunchConfirm />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/biler" element={<Cars />} />
        <Route path="/biler/:brandSlug/:modelSlug" element={<ModelDetail />} />
        <Route path="/biler/:brandSlug/:modelSlug/:variantSlug" element={<CarDetail />} />
        <Route path="/mine-biler" element={<MyCars />} />
        <Route path="/sammenlign" element={<Compare />} />
        <Route path="/maerker" element={<Brands />} />
        <Route path="/maerker/:brandSlug" element={<BrandDetail />} />
        <Route path="/nyheder" element={<News />} />
        <Route path="/nyheder/:slug" element={<NewsDetail />} />
        <Route path="/kampagner" element={<Campaigns />} />
        <Route path="/anmeldelser" element={<Reviews />} />
        <Route path="/anmeldelser/udgiver/:slug" element={<PublisherReviews />} />
        <Route path="/forhandler" element={<DealerLanding />} />
        <Route path="/forhandler/kampagner" element={<DealerDashboard />} />
        <Route path="/forhandler/aktiver" element={<DealerActivate />} />
        <Route path="/forhandler/onboarding" element={<DealerOnboarding />} />
        <Route path="/forhandler/portal" element={<DealerPortal />} />
        <Route path="/forhandler/portal/variant/:variantId" element={<DealerEditVariant />} />
        <Route path="/okonomi" element={<Okonomi />} />
        <Route path="/find-bil" element={<FindCar />} />
        <Route path="/bilhjælper" element={<Navigate to="/find-bil" replace />} />
        <Route path="/bilhjaelper" element={<Navigate to="/find-bil" replace />} />
        <Route path="/bidrag" element={<Contribute />} />
        <Route path="/nyhedsbrev" element={<Newsletter />} />
        <Route path="/nyhedsbrev/bekraeft" element={<NewsletterConfirm />} />
        <Route path="/nyhedsbrev/afmeld" element={<NewsletterUnsubscribe />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/persondatapolitik" element={<Persondatapolitik />} />
        <Route path="/privatlivspolitik" element={<Navigate to="/persondatapolitik" replace />} />
        <Route path="/vilkar" element={<Vilkar />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/om-min-nye-elbil" element={<About />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/ofte-stillede-spoergsmaal" element={<Navigate to="/faq" replace />} />
        <Route path="/vaerktoejer" element={<Vaerktoejer />} />
        <Route path="/partnere" element={<Navigate to="/vaerktoejer" replace />} />
        <Route path="/magasin" element={<Magazine />} />
        <Route path="/magasin/:slug" element={<MagazineIssue />} />


        {/* Community */}
        <Route path="/community" element={<CommunityHome />} />
        <Route path="/community/sporgsmaal/nyt" element={<AskQuestion />} />
        <Route path="/community/sporgsmaal/:id/:slug" element={<PostDetail />} />
        <Route path="/community/sporgsmaal/:id" element={<PostDetail />} />
        <Route path="/community/erfaringer" element={<Experiences />} />
        <Route path="/community/erfaringer/ny" element={<ShareExperience />} />
        <Route path="/community/eksperter" element={<Experts />} />
        <Route path="/community/eksperter/sadan-bliver-du" element={<HowToBecomeExpert />} />
        <Route path="/community/eksperter/bliv" element={<Navigate to="/community/eksperter/sadan-bliver-du" replace />} />
        <Route path="/community/eksperter/:username" element={<UserProfile />} />
        <Route path="/community/model/:brandSlug/:modelSlug" element={<ModelCommunity />} />
        <Route path="/community/profil/mig" element={<MyProfile />} />
        <Route path="/community/profil/:username" element={<UserProfile />} />
        
        {/* Admin routes */}
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/oversigt" element={<ProtectedRoute><AdminOverview /></ProtectedRoute>} />
        <Route path="/admin/maerker" element={<ProtectedRoute><AdminBrands /></ProtectedRoute>} />
        <Route path="/admin/modeller" element={<ProtectedRoute><AdminModels /></ProtectedRoute>} />
        <Route path="/admin/varianter" element={<ProtectedRoute><AdminVariants /></ProtectedRoute>} />
        <Route path="/admin/priser" element={<ProtectedRoute><AdminPrices /></ProtectedRoute>} />
        <Route path="/admin/anmeldelser" element={<ProtectedRoute><AdminReviews /></ProtectedRoute>} />
        <Route path="/admin/anmeldelse-forslag" element={<ProtectedRoute><AdminReviewSubmissions /></ProtectedRoute>} />
        <Route path="/admin/nyheder" element={<ProtectedRoute><AdminNews /></ProtectedRoute>} />
        <Route path="/admin/import" element={<ProtectedRoute><AdminImport /></ProtectedRoute>} />
        <Route path="/admin/csv-import" element={<ProtectedRoute><AdminCsvImport /></ProtectedRoute>} />
        <Route path="/admin/import-review" element={<ProtectedRoute><AdminImportReview /></ProtectedRoute>} />
        <Route path="/admin/datahentning" element={<ProtectedRoute><AdminEtl /></ProtectedRoute>} />
        <Route path="/admin/inspirationsfiltre" element={<ProtectedRoute><AdminInspirationFilters /></ProtectedRoute>} />
        <Route path="/admin/kampagner" element={<ProtectedRoute><AdminCampaigns /></ProtectedRoute>} />
        <Route path="/admin/medieovervaagning" element={<ProtectedRoute><AdminMediaMonitor /></ProtectedRoute>} />
        <Route path="/admin/forhandlerbidrag" element={<ProtectedRoute><AdminDealerContributions /></ProtectedRoute>} />
        <Route path="/admin/forhandler-kampagner" element={<ProtectedRoute><AdminDealerCampaigns /></ProtectedRoute>} />
        <Route path="/admin/analyse" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />
        <Route path="/admin/feedback" element={<ProtectedRoute><AdminFeedback /></ProtectedRoute>} />
        <Route path="/admin/fejl-i-formularer" element={<ProtectedRoute><AdminSubmissionErrors /></ProtectedRoute>} />
        <Route path="/admin/visning" element={<ProtectedRoute><AdminDisplaySettings /></ProtectedRoute>} />
        <Route path="/admin/sidetekster" element={<ProtectedRoute><AdminSiteSettings /></ProtectedRoute>} />
        <Route path="/admin/dataforslag" element={<ProtectedRoute><AdminChangeRequests /></ProtectedRoute>} />
        <Route path="/admin/ideer" element={<ProtectedRoute><AdminIdeas /></ProtectedRoute>} />
        <Route path="/admin/crawler" element={<ProtectedRoute><AdminCrawl /></ProtectedRoute>} />
        <Route path="/admin/forhandlerlinks" element={<ProtectedRoute><AdminDealerLinks /></ProtectedRoute>} />
        <Route path="/admin/community" element={<ProtectedRoute><AdminCommunity /></ProtectedRoute>} />
        <Route path="/admin/brugere" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
        <Route path="/admin/ekspertansoegninger" element={<ProtectedRoute><AdminExpertApplications /></ProtectedRoute>} />
        <Route path="/admin/nyhedsbrev" element={<ProtectedRoute><AdminNewsletter /></ProtectedRoute>} />
        <Route path="/admin/daekning" element={<ProtectedRoute><AdminCoverage /></ProtectedRoute>} />
        <Route path="/admin/datakvalitet" element={<ProtectedRoute><AdminDataQuality /></ProtectedRoute>} />
        <Route path="/admin/opgaver" element={<ProtectedRoute><AdminTasks /></ProtectedRoute>} />
        <Route path="/admin/landingssider" element={<ProtectedRoute><AdminLandingPages /></ProtectedRoute>} />

        <Route path="/admin/magasin" element={<ProtectedRoute><AdminMagazine /></ProtectedRoute>} />
        <Route path="/admin/annoncer" element={<ProtectedRoute><AdminAffiliates /></ProtectedRoute>} />

        
        {/* 404 */}
        <Route path="/guides" element={<Guides />} />
        <Route path="/guides/:slug" element={<LandingPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

const App = () => (
  <SentryErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CompareProvider>
          <TooltipProvider>
            <Sonner />
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <ScrollToTop />
              <AppRoutes />
              <CompareBar />
              <VersionChecker />
              <CookieBanner />
            </BrowserRouter>
          </TooltipProvider>
        </CompareProvider>
      </AuthProvider>
    </QueryClientProvider>
  </SentryErrorBoundary>
);

export default App;
